import React, { useState } from 'react';
import {
    ConfigProvider,
    theme,
    Input,
    Select,
    DatePicker,
    Button,
    Upload,
    Checkbox,
    InputNumber,
} from 'antd';
import {
    ChevronDown,
    ChevronRight,
    Search,
    Calendar,
    X,
    RefreshCw,
    UploadCloud,
    Folder,
    Check
} from 'lucide-react';
import { LeftOutlined } from "@ant-design/icons";
import Layout from '../../Layout';
import { Link } from "react-router-dom";

export default function AddProjectForm() {
    const [isDarkMode, setIsDarkMode] = useState(false);

    // ================= STATES =================
    const [expandedSections, setExpandedSections] = useState({
        general: true,
        permissions: true,
        tags: true,
    });

    const toggleSection = (key) => {
        setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
    };

    // Form values state
    const [projectCode, setProjectCode] = useState('DA_16/09/2026_79');
    const [projectName, setProjectName] = useState('');
    const [projectGroup, setProjectGroup] = useState(undefined);
    const [progressMethod, setProgressMethod] = useState('Theo bình quân % tiến độ các công việc thuộc dự án');
    
    // Custom Color Picker states
    const [selectedColor, setSelectedColor] = useState('#22c55e'); // Mặc định xanh lá như hình
    const [showColorDropdown, setShowColorDropdown] = useState(false);
    
    // Avatar upload preview state
    const [avatarUrl, setAvatarUrl] = useState(null);

    // Dropdown visibility states
    const [showProgressDropdown, setShowProgressDropdown] = useState(false);
    const [showGroupDropdown, setShowGroupDropdown] = useState(false);

    const projectGroups = [
        '(Nguyệt) Nhóm dự án tháng 5-7',
        '03.XDKM',
        'ABC',
        'AVP_Demo_DA',
        'Bảo Trì & Giám Sát',
        'Bất Động Sản',
        'Chăm sóc khách hàng',
        'chuẩn bị kiểm tra TB tháng 10',
        'Công nghệ thông tin'
    ];

    const colorPalette = [
        '#ef4444', '#f97316', '#f59e0b', '#84cc16', '#22c55e', 
        '#06b6d4', '#3e91ab', '#3b82f6', '#6366f1', '#a855f7', '#ec4899'
    ];

    const progressMethods = [
        {
            title: 'Theo bình quân % tiến độ các công việc thuộc dự án',
            desc: 'Ví dụ dự án gồm 2 công việc A và B.\nCông việc A tiến độ 40%, công việc B tiến độ 60%.\nTiến độ dự án là (60+40)/2 = 50%'
        },
        {
            title: 'Theo tỷ trọng ngày thực hiện',
            desc: 'Ví dụ dự án gồm 2 công việc A và B.\nCông việc A yêu cầu thực hiện trong 4 ngày, tiến độ 40%\nCông việc B yêu cầu thực hiện trong 6 ngày, tiến độ 50%\nTiến độ dự án là ((4*40 + 6*50 )/(4*100 + 6*100)) * 100 = 46%'
        },
        {
            title: 'Theo tỷ trọng công việc',
            desc: 'Ví dụ Dự án gồm 2 công việc A và B\nCông việc A có Tỷ trọng là 40, tiến độ là 50%\nCông việc B có Tỷ trọng là 30, tiến độ là 40%\nTiến độ của dự án là [(40x50)+(30x40)]/(40+30)=45.71%'
        }
    ];

    const tagOptions = [
        'Quan trọng', 'Trúng thầu', '[GTV] Chuẩn', 'Cấp xóa/Thay đổi', 
        'Đang cập nhật', '[1Office] Chuẩn không xóa/điều chỉnh', 'test', 
        'XDKM', 'AHN', 'Nghiệm thu', 'Test QT', 'NMN', 'NTL'
    ];

    const antdThemeConfig = {
        token: {
            colorPrimary: '#3e91ab',
            colorBgContainer: isDarkMode ? '#1f2937' : '#ffffff',
            colorText: isDarkMode ? '#f3f4f6' : '#374151',
            colorBorder: isDarkMode ? '#4b5563' : '#d1d5db',
            borderRadius: 6,
            controlHeight: 40,
        },
        algorithm: isDarkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
    };

    const SectionHeader = ({ title, sectionKey }) => (
        <div
            onClick={() => toggleSection(sectionKey)}
            className="flex items-center gap-2 text-[#3e91ab] font-medium text-sm mb-4 mt-8 first:mt-0 cursor-pointer hover:opacity-80 transition-opacity select-none"
        >
            {expandedSections[sectionKey] ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            <span>{title}</span>
        </div>
    );

    // Xử lý upload ảnh đại diện preview
    const handleAvatarChange = (info) => {
        const file = info.file.originFileObj || info.file;
        const reader = new FileReader();
        reader.onload = (e) => {
            setAvatarUrl(e.target.result);
        };
        if (file) {
            reader.readAsDataURL(file);
        }
    };

    return (
        <Layout>
            <div className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-4 md:px-6 py-4 sticky top-[64px] z-10 shadow-sm">
                <div className="flex items-center gap-2 text-sm">
                    <Link to="/projects/list" className="flex items-center gap-2">
                        <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#3e91ab] hover:bg-[#357f96] text-white">
                            <LeftOutlined className="text-[10px]" />
                        </div>
                    </Link>
                    <span className="text-gray-800 dark:text-gray-200 font-medium">Tạo mới dự án</span>
                </div>
            </div>

            <ConfigProvider theme={antdThemeConfig}>
                <div className="pb-10 transition-colors duration-300">
                    <div className="w-full mx-auto rounded-lg shadow-sm p-4 md:p-8 mt-6 dark:bg-gray-800 bg-white border dark:border-gray-700">

                        {/* --- 1. THÔNG TIN CHUNG --- */}
                        <SectionHeader title="Thông tin chung" sectionKey="general" />
                        {expandedSections.general && (
                            <div className="flex flex-col gap-4 animate-in slide-in-from-top-2 mb-6">

                                {/* Mã dự án & Tên dự án & Bảng chọn màu (Color Picker) */}
                                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                                    <div className="md:col-span-5">
                                        <label className="text-xs text-gray-500 mb-1 block">Mã dự án <span className="text-red-500">*</span></label>
                                        <Input 
                                            value={projectCode} 
                                            onChange={(e) => setProjectCode(e.target.value)} 
                                            suffix={<RefreshCw size={16} className="text-gray-400 cursor-pointer hover:text-[#3e91ab]" onClick={() => setProjectCode(`DA_${Date.now()}`)} />} 
                                        />
                                    </div>
                                    <div className="md:col-span-6">
                                        <label className="text-xs text-gray-500 mb-1 block">Tên dự án <span className="text-red-500">*</span></label>
                                        <Input placeholder="Nhập tên dự án" value={projectName} onChange={(e) => setProjectName(e.target.value)} />
                                    </div>
                                    
                                    {/* Cục chọn màu hiển thị bảng màu khi click */}
                                    <div className="md:col-span-1 relative">
                                        <div 
                                            onClick={() => setShowColorDropdown(!showColorDropdown)}
                                            className="border dark:border-gray-600 rounded-md px-3 bg-white dark:bg-gray-700 cursor-pointer flex justify-between items-center h-[40px] shadow-sm hover:border-[#3e91ab]"
                                        >
                                            <span className="w-4 h-4 rounded-full inline-block" style={{ backgroundColor: selectedColor }}></span>
                                            <ChevronDown size={14} className="text-gray-400" />
                                        </div>

                                        {showColorDropdown && (
                                            <div className="absolute right-0 top-full mt-1 bg-white dark:bg-gray-800 border dark:border-gray-700 p-3 rounded-lg shadow-xl z-30 grid grid-cols-4 gap-2 w-44">
                                                {colorPalette.map((color, idx) => (
                                                    <div 
                                                        key={idx}
                                                        onClick={() => { setSelectedColor(color); setShowColorDropdown(false); }}
                                                        className="w-8 h-8 rounded-full cursor-pointer flex items-center justify-center transition hover:scale-110 shadow-sm"
                                                        style={{ backgroundColor: color }}
                                                    >
                                                        {selectedColor === color && <Check size={14} className="text-white" />}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Ngày bắt đầu, kết thúc, và Số ngày thực hiện dự án */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="text-xs text-gray-500 mb-1 block">Bắt đầu</label>
                                        <DatePicker className="w-full" format="DD/MM/YYYY" placeholder="Chọn ngày" suffixIcon={<Calendar size={16} className="text-gray-400" />} />
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-500 mb-1 block">Kết thúc</label>
                                        <DatePicker className="w-full" format="DD/MM/YYYY" placeholder="Chọn ngày" suffixIcon={<Calendar size={16} className="text-gray-400" />} />
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-500 mb-1 block">Ngày</label>
                                        <InputNumber className="w-full" placeholder="Nhập số ngày thực hiện" min={0} defaultValue={1} />
                                    </div>
                                </div>

                                {/* Nhóm dự án & Tổng giá trị */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 relative">
                                    <div>
                                        <label className="text-xs text-gray-500 mb-1 block">Nhóm dự án</label>
                                        <div 
                                            onClick={() => setShowGroupDropdown(!showGroupDropdown)}
                                            className="border dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 cursor-pointer flex justify-between items-center h-[40px]"
                                        >
                                            <span className="text-sm text-gray-700 dark:text-gray-200">{projectGroup || 'Chọn nhóm dự án'}</span>
                                            <ChevronDown size={16} className="text-gray-400" />
                                        </div>
                                        {showGroupDropdown && (
                                            <div className="absolute top-full left-0 w-full bg-white dark:bg-gray-800 border dark:border-gray-700 shadow-lg rounded-md z-20 mt-1 max-h-60 overflow-y-auto p-2">
                                                <Input placeholder="Tìm kiếm..." prefix={<Search size={14} className="text-gray-400 mr-2" />} className="mb-2" />
                                                {projectGroups.map((group, idx) => (
                                                    <div 
                                                        key={idx} 
                                                        onClick={() => { setProjectGroup(group); setShowGroupDropdown(false); }}
                                                        className="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded cursor-pointer text-xs"
                                                    >
                                                        <div className="font-medium text-gray-800 dark:text-gray-200">{group}</div>
                                                        <div className="text-[10px] text-gray-400 italic">Cho phép quản lý tiến độ</div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-500 mb-1 block">Tổng giá trị dự án</label>
                                        <Input placeholder="Tổng giá trị dự án" />
                                    </div>
                                </div>

                                {/* Cách tính tiến độ dự án */}
                                <div className="relative">
                                    <label className="text-xs text-gray-500 mb-1 block">Cách tính tiến độ dự án <span className="text-red-500">*</span></label>
                                    <div 
                                        onClick={() => setShowProgressDropdown(!showProgressDropdown)}
                                        className="border border-[#3e91ab] rounded-md px-3 py-2 bg-white dark:bg-gray-700 cursor-pointer flex justify-between items-center h-[40px] shadow-sm"
                                    >
                                        <span className="text-sm text-gray-800 dark:text-gray-200">{progressMethod}</span>
                                        <div className="flex items-center gap-2">
                                            <X size={14} className="text-gray-400 hover:text-gray-600" onClick={(e) => { e.stopPropagation(); setProgressMethod(''); }} />
                                            <ChevronDown size={16} className="text-gray-400" />
                                        </div>
                                    </div>

                                    {showProgressDropdown && (
                                        <div className="absolute top-full left-0 w-full bg-white dark:bg-gray-800 border dark:border-gray-700 shadow-xl rounded-md z-20 mt-1 p-3 flex flex-col gap-3">
                                            {progressMethods.map((m, idx) => (
                                                <div 
                                                    key={idx}
                                                    onClick={() => { setProgressMethod(m.title); setShowProgressDropdown(false); }}
                                                    className="p-3 bg-gray-50 dark:bg-gray-700/50 hover:bg-[#3e91ab]/10 rounded-md cursor-pointer transition"
                                                >
                                                    <div className="font-medium text-xs text-gray-800 dark:text-gray-100 mb-1">{m.title}</div>
                                                    <div className="text-[11px] text-gray-500 dark:text-gray-400 whitespace-pre-line italic">{m.desc}</div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Người quản trị / Người thực hiện / Người theo dõi */}
                                <div>
                                    <label className="text-xs text-gray-500 mb-1 block">Người quản trị <span className="text-red-500">*</span></label>
                                    <Select 
                                        mode="multiple"
                                        placeholder="Chọn người quản trị"
                                        className="w-full"
                                        defaultValue={['HQ_456 - Vũ Ngọc B', '6 - Nguyễn G', '5 - Nguyễn E']}
                                        options={[
                                            { value: 'HQ_456 - Vũ Ngọc B', label: 'HQ_456 - Vũ Ngọc B' },
                                            { value: '6 - Nguyễn G', label: '6 - Nguyễn G' },
                                            { value: '5 - Nguyễn E', label: '5 - Nguyễn E' },
                                        ]}
                                    />
                                </div>

                                <div>
                                    <label className="text-xs text-gray-500 mb-1 block">Người thực hiện</label>
                                    <Select 
                                        mode="multiple"
                                        placeholder="Chọn người thực hiện"
                                        className="w-full"
                                        defaultValue={['A8901 - Nguyễn Văn Huynh', 'A8909 - Nguyễn Đức Hải']}
                                        options={[
                                            { value: 'A8901 - Nguyễn Văn Huynh', label: 'A8901 - Nguyễn Văn Huynh' },
                                            { value: 'A8909 - Nguyễn Đức Hải', label: 'A8909 - Nguyễn Đức Hải' },
                                        ]}
                                    />
                                </div>

                                <div>
                                    <label className="text-xs text-gray-500 mb-1 block">Người theo dõi</label>
                                    <Select 
                                        mode="multiple"
                                        placeholder="Chọn người theo dõi"
                                        className="w-full"
                                        defaultValue={['HQ_457 - Vũ Ngọc C', 'HQ_456 - Vũ Ngọc B']}
                                        options={[
                                            { value: 'HQ_457 - Vũ Ngọc C', label: 'HQ_457 - Vũ Ngọc C' },
                                            { value: 'HQ_456 - Vũ Ngọc B', label: 'HQ_456 - Vũ Ngọc B' },
                                        ]}
                                    />
                                </div>

                                {/* Mô tả */}
                                <div>
                                    <label className="text-xs text-gray-500 mb-1 block">Mô tả</label>
                                    <Input.TextArea rows={3} placeholder="Nhập mô tả dự án" />
                                </div>

                                {/* Ảnh đại diện (Hiển thị preview ảnh khi chọn) */}
                                <div>
                                    <label className="text-xs text-gray-500 mb-1 block">Ảnh đại diện</label>
                                    <Upload 
                                        showUploadList={false}
                                        beforeUpload={() => false}
                                        onChange={handleAvatarChange}
                                    >
                                        <div className="w-24 h-24 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex items-center justify-center bg-gray-50 dark:bg-gray-700/30 cursor-pointer hover:border-[#3e91ab] overflow-hidden relative">
                                            {avatarUrl ? (
                                                <img src={avatarUrl} alt="Avatar Preview" className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="text-gray-400 flex flex-col items-center">
                                                    <ChevronDown size={20} className="rotate-180" />
                                                </div>
                                            )}
                                        </div>
                                    </Upload>
                                </div>

                                {/* Đính kèm (Chuẩn giao diện kéo thả mẫu gốc) */}
                                <div>
                                    <label className="text-xs text-gray-500 mb-1 block">Đính kèm</label>
                                    <div className="border border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-700/30">
                                        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-12 h-12 rounded-full border border-orange-200 text-orange-500 flex items-center justify-center bg-orange-50 shrink-0">
                                                    <UploadCloud size={24} />
                                                </div>
                                                <span className="text-xs text-gray-500 dark:text-gray-300">Kéo thả file vào đây để tải lên hoặc</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Button type="primary" style={{ backgroundColor: '#f97316', borderColor: '#f97316' }}>
                                                    CHỌN TỪ MÁY
                                                </Button>
                                                <Button icon={<Folder size={14} />}>
                                                    CHỌN TỪ CLOUD
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* --- 2. CÀI ĐẶT QUYỀN --- */}
                        <SectionHeader title="Cài đặt quyền" sectionKey="permissions" />
                        {expandedSections.permissions && (
                            <div className="flex flex-col gap-3 mb-6 animate-in slide-in-from-top-2">
                                <Checkbox defaultChecked className="text-xs">Khi thời gian thực hiện dự án thay đổi thì thời gian công việc thay đổi theo</Checkbox>
                                <Checkbox defaultChecked className="text-xs">Không cho phép người thực hiện công việc xem chéo các công việc khác</Checkbox>
                                <Checkbox defaultChecked className="text-xs">Không cho phép người theo dõi xem được các công việc con</Checkbox>
                                <Checkbox defaultChecked className="text-xs">Thời gian dự kiến thực hiện công việc phải nằm trong khoảng thời gian của dự án</Checkbox>
                            </div>
                        )}

                        {/* --- 3. NHÃN --- */}
                        <SectionHeader title="Nhãn" sectionKey="tags" />
                        {expandedSections.tags && (
                            <div className="mb-6 animate-in slide-in-from-top-2">
                                <Select
                                    mode="multiple"
                                    placeholder="Chọn nhãn"
                                    className="w-full"
                                    options={tagOptions.map(t => ({ value: t, label: t }))}
                                />
                            </div>
                        )}

                        {/* --- FOOTER BUTTONS --- */}
                        <div className="flex gap-3 pt-6 mt-10 border-t dark:border-gray-700 sticky bottom-2">
                            <Button type="primary" style={{ backgroundColor: '#3e91ab', borderColor: '#3e91ab', fontWeight: 500 }}>
                                CẬP NHẬT
                            </Button>
                            <Button className="font-medium">
                                HỦY BỎ
                            </Button>
                        </div>
                    </div>
                </div>
            </ConfigProvider>
        </Layout>
    );
}